/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

import {
    Component,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { MetricsDisplayValueType } from 'ng/root-store/user-preferences/user-preferences.state';
import { L10nService, VIPModule } from '@vmw/ngx-vip';
import {
    selectValuesToDisplay,
    updateUIProperty,
} from 'ng/root-store/user-preferences';
import { AviDropdownComponent, IAviDropdownOption } from 'standalone-components/avi-dropdown';
import * as globalL10n from 'global-l10n';
import * as l10n from './metric-values-selector.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description
 * Dropdown for metric values to display. This component interacts directly with the root store, so
 * any component that uses this component should subscribe to the store value for the latest value.
 * @author alextsg
 */
@Component({
    selector: 'metric-values-selector',
    templateUrl: './metric-values-selector.component.html',
    standalone: true,
    imports: [
        VIPModule,
        AviDropdownComponent,
        FormsModule,
    ],
})
export class MetricValuesSelectorComponent implements OnInit, OnDestroy {
    /**
     * Dropdown options containing valuesToDisplay values.
     */
    public readonly options: Array<IAviDropdownOption<MetricsDisplayValueType>>;

    public metricValue: MetricsDisplayValueType = MetricsDisplayValueType.AVERAGE;

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    private valuesToDisplaySubscription: Subscription;

    constructor(
        private readonly l10nService: L10nService,
        private readonly store: Store,
    ) {
        l10nService.registerSourceBundles(dictionary);

        this.options = [
            {
                value: MetricsDisplayValueType.AVERAGE,
                label: this.l10nService.getMessage(l10nKeys.averageLabel),
            },
            {
                value: MetricsDisplayValueType.CURRENT,
                label: this.l10nService.getMessage(l10nKeys.currentLabel),
            },
            {
                value: MetricsDisplayValueType.MAX,
                label: this.l10nService.getMessage(l10nKeys.peakLabel),
            },
            {
                value: MetricsDisplayValueType.SUM,
                label: this.l10nService.getMessage(l10nKeys.totalLabel),
            },
        ];
    }

    /**
     * @override
     * Sets subscription to update the valuesToDisplay value from the root store.
     */
    public ngOnInit(): void {
        this.valuesToDisplaySubscription = this.store.select(selectValuesToDisplay)
            .subscribe(valuesToDisplay => this.metricValue = valuesToDisplay);
    }

    /**
     * @override
     * Unsubscribes from the store subscription.
     */
    public ngOnDestroy(): void {
        this.valuesToDisplaySubscription.unsubscribe();
    }

    /**
     * Called when user changes the dropdown value. Saves the selected value in user preferences.
     */
    public handleChange(): void {
        this.store.dispatch(updateUIProperty({
            payload: {
                valuesToDisplay: this.metricValue,
            },
        }));
    }
}
